import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, titleCase, dOnly } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea } from "../ui";

const EMPTY = {
    code: "", name: "", discount_type: "percentage", value: "", scope: "all",
    first_order_only: false, min_order_value: 0, max_discount: "", starts_at: "", ends_at: "",
    usage_limit: "", per_customer_limit: 1, is_active: true, description: "",
    product_ids: [], divisions: [],
};

export default function RhmOffers() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState(null);

    const { data: offers = [], isLoading } = useQuery({
        queryKey: ["rhm-offers"],
        queryFn: async () => (await rhm.get("/offers")).data,
    });

    const save = useMutation({
        mutationFn: async (payload) => {
            const body = {
                ...payload,
                value: Number(payload.value || 0),
                min_order_value: Number(payload.min_order_value || 0),
                max_discount: payload.max_discount === "" ? null : Number(payload.max_discount),
                usage_limit: payload.usage_limit === "" ? null : Number(payload.usage_limit),
                per_customer_limit: payload.per_customer_limit === "" ? null : Number(payload.per_customer_limit),
                starts_at: payload.starts_at || null,
                ends_at: payload.ends_at || null,
            };
            return payload.id
                ? (await rhm.patch(`/offers/${payload.id}`, body)).data
                : (await rhm.post("/offers", body)).data;
        },
        onSuccess: () => { toast.success("Offer saved"); setForm(null); qc.invalidateQueries({ queryKey: ["rhm-offers"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    return (
        <div data-testid="rhm-offers-page">
            <PageHead title="Offers & Discounts"
                sub="Prices are never hard-coded — checkout always recalculates from these rules."
                action={can("offers.create") && (
                    <Btn onClick={() => setForm(EMPTY)} data-testid="rhm-add-offer">
                        <Plus className="w-3.5 h-3.5 inline mr-1.5" /> NEW OFFER
                    </Btn>
                )} />

            <Card title={`${offers.length} offer(s)`}>
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-offers-table"
                        head={["Code", "Name", "Discount", "Scope", "First Order", "Window", "Used", "Status", ""]}
                        empty={!offers.length && <Empty msg="No offers yet." />}>
                        {offers.map((o) => (
                            <tr key={o.id} data-testid={`rhm-offer-row-${o.code}`}>
                                <Td className="mono text-xs text-gold">{o.code}</Td>
                                <Td className="font-semibold">{o.name}</Td>
                                <Td>{o.discount_type === "percentage" ? `${o.value}%` : `₹${o.value}`}</Td>
                                <Td className="text-sm text-zinc-400">{titleCase(o.scope)}</Td>
                                <Td><Badge tone={o.first_order_only ? "gold" : "muted"}>{o.first_order_only ? "YES" : "NO"}</Badge></Td>
                                <Td className="text-xs text-zinc-500">
                                    {o.starts_at ? dOnly(o.starts_at) : "Always"} → {o.ends_at ? dOnly(o.ends_at) : "Open"}
                                </Td>
                                <Td>{o.used_count || 0}</Td>
                                <Td><Badge tone={o.is_active ? "good" : "danger"}>{o.is_active ? "ACTIVE" : "OFF"}</Badge></Td>
                                <Td>
                                    {can("offers.edit") && (
                                        <button onClick={() => setForm({ ...o, starts_at: (o.starts_at || "").slice(0, 10), ends_at: (o.ends_at || "").slice(0, 10) })}
                                            data-testid={`rhm-edit-offer-${o.code}`}
                                            className="mono text-[10px] tracking-[0.16em] text-gold">EDIT</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Modal open={!!form} onClose={() => setForm(null)} wide testId="rhm-offer-modal"
                title={form?.id ? `Edit ${form.code}` : "New Offer"}>
                {form && (
                    <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-5">
                        <div className="grid md:grid-cols-3 gap-4">
                            <Field label="Coupon / offer code" required>
                                <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="offer-code" />
                            </Field>
                            <Field label="Display name" required>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offer-name" />
                            </Field>
                            <Field label="Status">
                                <Select value={form.is_active ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "yes" })}
                                    options={[{ value: "yes", label: "Active" }, { value: "no", label: "Inactive" }]} data-testid="offer-status" />
                            </Field>
                            <Field label="Discount type">
                                <Select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                                    options={[{ value: "percentage", label: "Percentage" }, { value: "fixed", label: "Fixed amount" }]} data-testid="offer-type" />
                            </Field>
                            <Field label="Value" required hint="50 = 50% or ₹50">
                                <Input type="number" min="0" step="0.01" required value={form.value}
                                    onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="offer-value" />
                            </Field>
                            <Field label="Maximum discount (₹)" hint="Optional cap">
                                <Input type="number" min="0" value={form.max_discount ?? ""} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} />
                            </Field>
                            <Field label="Scope">
                                <Select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                                    options={[{ value: "all", label: "All products" }, { value: "product", label: "Specific products" }, { value: "division", label: "Division" }]} />
                            </Field>
                            <Field label="First order only">
                                <Select value={form.first_order_only ? "yes" : "no"} onChange={(e) => setForm({ ...form, first_order_only: e.target.value === "yes" })}
                                    options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} data-testid="offer-first-order" />
                            </Field>
                            <Field label="Minimum order value (₹)">
                                <Input type="number" min="0" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} />
                            </Field>
                            <Field label="Starts on"><Input type="date" value={form.starts_at || ""} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></Field>
                            <Field label="Ends on"><Input type="date" value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></Field>
                            <Field label="Total usage limit" hint="Blank = unlimited">
                                <Input type="number" min="0" value={form.usage_limit ?? ""} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
                            </Field>
                            <Field label="Description" className="md:col-span-3">
                                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            </Field>
                        </div>
                        <div className="flex gap-3">
                            <Btn type="submit" disabled={save.isPending} data-testid="offer-submit">SAVE OFFER</Btn>
                            <Btn type="button" variant="ghost" onClick={() => setForm(null)}>CANCEL</Btn>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}
